import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Input,
  Button,
  List,
  Avatar,
  Typography,
  Space,
  Alert,
  Spin,
  message,
  Badge,
} from 'antd';
import {
  RobotOutlined,
  UserOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import { ArtifactReviewDialog } from './ArtifactReviewDialog';
import { FocusAreaSelector } from './FocusAreaSelector';
import { useUser } from '../../../contexts/UserContext';
import { apiClient } from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../services/api/config';
import type { 
  ChatMessageResponse, 
  ChatTurnRequest, 
  ChatThreadResponse, 
  KrybotApproval,
  KrybotAgentRun,
} from '../../../types/krybot';
import './PrimeAgentChat.css';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

interface PrimeAgentChatProps {
  artifactId: string;
  artifactName: string;
  userId: string;
  onApprovalDecision?: () => void;
}

export const PrimeAgentChat: React.FC<PrimeAgentChatProps> = ({
  artifactId,
  artifactName,
  userId,
  onApprovalDecision,
}) => {
  const [messages, setMessages] = useState<ChatMessageResponse[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thread, setThread] = useState<ChatThreadResponse | null>(null);
  const [loadingThread, setLoadingThread] = useState(true);
  const [pendingApproval, setPendingApproval] = useState<KrybotApproval | null>(null);
  const [pendingApprovalRun, setPendingApprovalRun] = useState<KrybotAgentRun | null>(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [loadingApprovalRun, setLoadingApprovalRun] = useState(false);
  const [focusAreas, setFocusAreas] = useState<Set<string>>(new Set());
  const [focusAreaStatus, setFocusAreaStatus] = useState<{
    properties: boolean;
    states: boolean;
    transitions: boolean;
    processes: boolean;
  }>({
    properties: false,
    states: false,
    transitions: false,
    processes: false,
  });
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const { user, isAuthenticated, isLoading: userLoading } = useUser();

  // Get actual user ID - prefer user from context, fallback to prop, then to default UUID
  const actualUserId = user?.id || userId || null;

  // Load approval status from artifact
  const loadApprovalStatus = async () => {
    if (!artifactId) return;
    try {
      const response = await apiClient.get(API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId));
      const artifact = response.data;
      const status = {
        properties: artifact.details_approved || false,
        states: artifact.states_approved || false,
        transitions: artifact.transitions_approved || false,
        processes: artifact.processes_approved || false,
      };
      setFocusAreaStatus(status);
      console.log('[PrimeAgentChat] Loaded approval status:', status);
    } catch (err) {
      console.error('[PrimeAgentChat] Failed to load approval status:', err);
      // Set defaults on error
      setFocusAreaStatus({
        properties: false,
        states: false,
        transitions: false,
        processes: false,
      });
    }
  };

  // Determine next unapproved step in sequence
  const getNextUnapprovedStep = (status: typeof focusAreaStatus): string | null => {
    if (!status.properties) return 'properties';
    if (!status.states) return 'states';
    if (!status.transitions) return 'transitions';
    if (!status.processes) return 'processes';
    return null; // All approved
  };

  // Auto-select next unapproved step when approval status changes
  useEffect(() => {
    const nextStep = getNextUnapprovedStep(focusAreaStatus);
    if (nextStep) {
      setFocusAreas(new Set([nextStep]));
      console.log('[PrimeAgentChat] Auto-selected next step:', nextStep);
    } else {
      // All steps approved, clear selection
      setFocusAreas(new Set());
    }
  }, [focusAreaStatus]);

  // Load approval status when artifactId changes
  useEffect(() => {
    if (artifactId) {
      loadApprovalStatus();
    }
  }, [artifactId]);

  // Initialize chat thread for this artifact
  useEffect(() => {
    const initializeChat = async () => {
      // Wait for user context to finish loading
      if (userLoading) {
        return;
      }

      // Check if user is authenticated and has a valid ID
      if (!isAuthenticated || !actualUserId || actualUserId === '00000000-0000-0000-0000-000000000000') {
        console.warn('PrimeAgentChat: No valid user ID available, skipping chat initialization');
        setLoadingThread(false);
        setError('Authentication required. Please log in to use the Prime Agent chat.');
        return;
      }

      try {
        setLoadingThread(true);
        // Bootstrap thread for prime artifact
        const bootstrap = await KrybotService.bootstrapThread({
          user_id: actualUserId,
          ui_role: 'architect',
          artifact_context: {
            artifact_id: artifactId,
            artifact_name: artifactName,
            artifact_type: 'prime',
          },
          metadata: {
            model: null,
          },
        });
        
        setThread(bootstrap.thread);
        setMessages(bootstrap.starter_messages || []);
        
        // Check for pending approval
        if (bootstrap.thread.pending_approval_id) {
          // Load approval details
          try {
            const approval = await KrybotService.getApproval(bootstrap.thread.pending_approval_id);
            setPendingApproval(approval);
            // If approval has agent_run_id, load the run
            if (approval.agent_run_id) {
              try {
                const run = await KrybotService.getAgentRun(approval.agent_run_id);
                setPendingApprovalRun(run);
                // Don't auto-open dialog on initial load - user will click button
              } catch (err) {
                console.error('Failed to load approval run:', err);
              }
            }
          } catch (err) {
            console.error('Failed to load pending approval:', err);
          }
        }
      } catch (err: any) {
        console.error('Failed to initialize chat:', err);
        setError(err.message || 'Failed to initialize chat');
        message.error(`Failed to initialize chat: ${err.message}`);
      } finally {
        setLoadingThread(false);
      }
    };

    initializeChat();
  }, [artifactId, artifactName, actualUserId, isAuthenticated, userLoading]);

  // Load chat history when thread changes
  useEffect(() => {
    if (thread?.id) {
      loadChatHistory();
    }
  }, [thread?.id]);

  // Ensure dialog shows when run is loaded after button click
  // This handles the case where showReviewDialog is set to true but pendingApprovalRun hasn't updated yet
  useEffect(() => {
    // If dialog should be shown but run isn't loaded yet, try to load it
    if (showReviewDialog && pendingApproval && !pendingApprovalRun && !loadingApprovalRun && pendingApproval.agent_run_id) {
      setLoadingApprovalRun(true);
      const loadRunAndShowDialog = async () => {
        try {
          console.log('[PrimeAgentChat] useEffect: Loading run for dialog:', pendingApproval.agent_run_id);
          const run = await KrybotService.getAgentRun(pendingApproval.agent_run_id!);
          console.log('[PrimeAgentChat] useEffect: Run loaded:', run?.id);
          setPendingApprovalRun(run);
          setLoadingApprovalRun(false);
          // Dialog will show once pendingApprovalRun is set (check in render condition)
        } catch (err) {
          console.error('[PrimeAgentChat] useEffect: Failed to load approval run:', err);
          message.error('Failed to load approval details. Please try again.');
          setShowReviewDialog(false); // Hide dialog if run can't be loaded
          setLoadingApprovalRun(false);
        }
      };
      loadRunAndShowDialog();
    }
  }, [showReviewDialog, pendingApproval, pendingApprovalRun, loadingApprovalRun]);

  const loadChatHistory = async () => {
    if (!thread?.id) return;
    
    try {
      const history = await KrybotService.getChatHistory(thread.id);
      setMessages(history.messages || []);
      
      // Update pending approval if thread has one
      if (history.thread.pending_approval_id) {
        try {
          const approval = await KrybotService.getApproval(history.thread.pending_approval_id);
          setPendingApproval(approval);
          // If approval has agent_run_id, load the run
          if (approval.agent_run_id) {
            try {
              const run = await KrybotService.getAgentRun(approval.agent_run_id);
              setPendingApprovalRun(run);
              // Don't auto-open dialog when loading history - user will click button
            } catch (err) {
              console.error('Failed to load approval run:', err);
            }
          }
        } catch (err) {
          console.error('Failed to load pending approval:', err);
        }
      } else {
        setPendingApproval(null);
        setPendingApprovalRun(null);
        setShowReviewDialog(false);
      }
    } catch (err: any) {
      console.error('Failed to load chat history:', err);
      message.error(`Failed to load chat history: ${err.message}`);
    }
  };

  // Check if user is at bottom of messages
  const checkIfAtBottom = useCallback(() => {
    if (!messagesContainerRef.current) return false;
    const container = messagesContainerRef.current;
    const threshold = 100; // pixels from bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  // Handle scroll events to track if user is at bottom
  const handleScroll = useCallback(() => {
    setIsUserAtBottom(checkIfAtBottom());
  }, [checkIfAtBottom]);

  // Scroll to bottom only if user is already at bottom
  const scrollToBottom = useCallback((force = false) => {
    if (force || isUserAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setIsUserAtBottom(true);
    }
  }, [isUserAtBottom]);

  // Track previous message count to detect new messages
  const prevMessageCountRef = useRef(0);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (messages.length > 0 && !loadingThread && prevMessageCountRef.current === 0) {
      // Initial load - force scroll to bottom
      setTimeout(() => {
        scrollToBottom(true);
        prevMessageCountRef.current = messages.length;
      }, 100);
    }
  }, [loadingThread, messages.length, scrollToBottom]);

  // Auto-scroll on new messages only if user is at bottom
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current && messages.length > 0) {
      // New message added - check if user is at bottom before scrolling
      const wasAtBottom = checkIfAtBottom();
      if (wasAtBottom) {
        setTimeout(() => {
          scrollToBottom(true);
        }, 50);
      }
      prevMessageCountRef.current = messages.length;
    }
  }, [messages.length, checkIfAtBottom, scrollToBottom]);

  // Toggle message expand/collapse
  const toggleMessageExpand = (messageId: string) => {
    setExpandedMessages((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  // Check if message should be collapsed (more than 3-4 lines)
  const shouldCollapseMessage = (content: string): boolean => {
    // Rough estimate: 3-4 lines ≈ 200-250 characters at typical width
    return content.length > 200;
  };

  const handleSend = async () => {
    if (!input.trim() || !thread?.id || !actualUserId) {
      if (!actualUserId) {
        setError('Authentication required. Please log in to send messages.');
      }
      return;
    }

    const userMessage: ChatMessageResponse = {
      id: `temp-${Date.now()}`,
      thread_id: thread.id,
      role: 'user',
      content: input,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setLoading(true);
    setError(null);

    try {
      // Include focus areas in message metadata
      const message_metadata: Record<string, unknown> = {};
      if (focusAreas.size > 0) {
        message_metadata.focus_areas = Array.from(focusAreas);
      }

      const payload: ChatTurnRequest = {
        thread_id: thread.id,
        user_id: actualUserId, // TypeScript now knows this is not null due to the guard above
        message_text: userInput,
        message_metadata: Object.keys(message_metadata).length > 0 ? message_metadata : undefined,
      };

      const response = await KrybotService.sendMessage(thread.id, payload);
      
      // Add assistant message
      if (response.assistant_message) {
        setMessages((prev) => [...prev, response.assistant_message!]);
      }
      
      // Update thread
      if (response.thread) {
        setThread(response.thread);
      }
      
      // Check for pending approval
      if (response.pending_approval) {
        // pending_approval is a summary, fetch full approval
        try {
          const approval = await KrybotService.getApproval(response.pending_approval.id);
          setPendingApproval(approval);
          // If approval has agent_run_id, load the run
          if (approval.agent_run_id) {
            try {
              const run = await KrybotService.getAgentRun(approval.agent_run_id);
              setPendingApprovalRun(run);
              // Don't auto-open dialog - user will click button to review
            } catch (err) {
              console.error('Failed to load approval run:', err);
              // Run failed to load - Alert will still show, user can try again
            }
          }
          // Don't auto-open dialog - user will click button to review
        } catch (err) {
          console.error('Failed to load approval details:', err);
        }
      } else if (response.thread.pending_approval_id) {
        // Load approval if referenced
        try {
          const approval = await KrybotService.getApproval(response.thread.pending_approval_id);
          setPendingApproval(approval);
          // If approval has agent_run_id, load the run
          if (approval.agent_run_id) {
            try {
              const run = await KrybotService.getAgentRun(approval.agent_run_id);
              setPendingApprovalRun(run);
              // Don't auto-open dialog - user will click button to review
            } catch (err) {
              console.error('Failed to load approval run:', err);
              // Run failed to load - Alert will still show, user can try again
            }
          }
          // Don't auto-open dialog - user will click button to review
        } catch (err) {
          console.error('Failed to load approval:', err);
        }
      } else {
        setPendingApproval(null);
        setPendingApprovalRun(null);
        setShowReviewDialog(false);
      }
      
      // Also check if response has agent_run and we don't have an approval yet
      if (response.agent_run && !response.pending_approval && !response.thread.pending_approval_id) {
        // Try to load approval from run (approval has agent_run_id that matches run.id)
        try {
          const approvals = await KrybotService.listApprovals({ status_filter: 'pending' });
          // Get full approval details to check agent_run_id
          for (const approvalSummary of approvals.approvals) {
            try {
              const fullApproval = await KrybotService.getApproval(approvalSummary.id);
              if (fullApproval.agent_run_id === response.agent_run?.id) {
                setPendingApproval(fullApproval);
                setPendingApprovalRun(response.agent_run);
                // Don't auto-open dialog - user will click button to review
                break;
              }
            } catch (err) {
              // Skip this approval and continue
              continue;
            }
          }
        } catch (err) {
          console.error('Failed to load approval from run:', err);
        }
      }
      
      // Note: We don't auto-open dialog based on llm_metadata - user must approve via approval workflow
    } catch (err: any) {
      console.error('Failed to send message:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to send message';
      setError(errorMessage);
      message.error(`Failed to send message: ${errorMessage}`);
      
      // Remove temporary user message on error
      setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalDecision = async () => {
    setShowReviewDialog(false);
    setPendingApproval(null);
    setPendingApprovalRun(null);
    
    // Reload chat history to get updated state first
    if (thread?.id) {
      await loadChatHistory();
    }
    
    // Wait a bit longer to ensure backend commit completes and DB is updated
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Reload approval status to reflect the updated flags
    if (artifactId) {
      await loadApprovalStatus();
      console.log('[PrimeAgentChat] Reloaded approval status after decision');
    }
    
    if (onApprovalDecision) {
      onApprovalDecision();
    }
  };

  if (userLoading || loadingThread) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Show authentication message if user is not logged in
  if (!isAuthenticated || !actualUserId) {
    return (
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>Prime Agent Chat - {artifactName}</span>
          </Space>
        }
      >
        <Alert
          message="Authentication Required"
          description={
            <div>
              <p>Please log in to use the Prime Agent chat feature.</p>
              <p style={{ marginTop: 8, fontSize: '12px', color: '#8c8c8c' }}>
                The Prime Agent chat requires a logged-in user to create chat threads and track conversation history.
              </p>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Card>
    );
  }

  return (
    <div className="prime-agent-chat">
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>Prime Agent Chat - {artifactName}</span>
            {pendingApproval && (
              <Badge count={1} status="processing">
                <Text type="warning">Pending Approval</Text>
              </Badge>
            )}
          </Space>
        }
        styles={{
          body: {
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 300px)',
            minHeight: '500px',
            padding: 0,
          },
        }}
      >
        {/* Messages area */}
        <div
          ref={messagesContainerRef}
          className="chat-messages"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            minHeight: 0,
          }}
          onScroll={handleScroll}
        >
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#8c8c8c' }}>
              <RobotOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <Paragraph>
                Start a conversation with the Prime Agent about {artifactName}.
                Ask questions or request changes to the artifact definition.
              </Paragraph>
            </div>
          )}

          <List
            dataSource={messages}
            renderItem={(msg) => {
              const isExpanded = expandedMessages.has(msg.id);
              const shouldCollapse = shouldCollapseMessage(msg.content || '');
              const showExpandButton = shouldCollapse && !isExpanded;
              const showCollapseButton = shouldCollapse && isExpanded;

              return (
                <List.Item style={{ border: 'none', padding: '12px 0' }}>
                  <Space align="start" style={{ width: '100%' }}>
                    <Avatar
                      icon={msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                      style={{
                        backgroundColor: msg.role === 'user' ? '#1890ff' : '#52c41a',
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <Space>
                        <Text strong>{msg.role === 'user' ? 'You' : 'Prime Agent'}</Text>
                        {msg.agent_run_id && (
                          <Badge status="processing" text="Agent Run" />
                        )}
                      </Space>
                      <div
                        className={`message-content ${isExpanded ? 'expanded' : 'collapsed'}`}
                        style={{
                          marginTop: 4,
                          marginBottom: 0,
                        }}
                      >
                        <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {msg.content}
                        </Paragraph>
                      </div>
                      {(showExpandButton || showCollapseButton) && (
                        <Button
                          type="link"
                          size="small"
                          icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                          onClick={() => toggleMessageExpand(msg.id)}
                          style={{
                            padding: 0,
                            height: 'auto',
                            fontSize: '12px',
                            marginTop: '4px',
                          }}
                        >
                          {isExpanded ? 'Show less' : 'Show more'}
                        </Button>
                      )}
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                        {new Date(msg.timestamp).toLocaleString()}
                      </Text>
                    </div>
                  </Space>
                </List.Item>
              );
            }}
          />

          {loading && (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Spin />
              <div style={{ marginTop: '8px' }}>Agent is thinking...</div>
            </div>
          )}

          {error && (
            <Alert
              message={error.includes('Authentication required') ? 'Authentication Required' : 'Error'}
              description={
                error.includes('Authentication required') ? (
                  <div>
                    <p>{error}</p>
                    <p style={{ marginTop: 8, fontSize: '12px', color: '#8c8c8c' }}>
                      The Prime Agent chat requires a logged-in user to create chat threads and track conversation history.
                    </p>
                  </div>
                ) : (
                  error
                )
              }
              type={error.includes('Authentication required') ? 'warning' : 'error'}
              closable
              onClose={() => setError(null)}
              style={{ marginTop: 16 }}
            />
          )}

          {pendingApproval && !showReviewDialog && (
            <Alert
              message="Pending Approval"
              description="There is a pending approval for proposed changes. Click 'Review Changes' to view and approve them."
              type="warning"
              showIcon
              action={
                <Button
                  size="small"
                  type="primary"
                  onClick={async () => {
                    console.log('[PrimeAgentChat] Review Changes button clicked', {
                      hasPendingApproval: !!pendingApproval,
                      hasPendingApprovalRun: !!pendingApprovalRun,
                      agent_run_id: pendingApproval?.agent_run_id,
                    });
                    
                    // Ensure we have the run loaded before showing dialog
                    let runToUse = pendingApprovalRun;
                    
                    if (!runToUse && pendingApproval?.agent_run_id) {
                      setLoadingApprovalRun(true);
                      try {
                        console.log('[PrimeAgentChat] Button handler: Loading run:', pendingApproval.agent_run_id);
                        const loadedRun = await KrybotService.getAgentRun(pendingApproval.agent_run_id);
                        console.log('[PrimeAgentChat] Button handler: Run loaded successfully:', loadedRun?.id);
                        setPendingApprovalRun(loadedRun);
                        setLoadingApprovalRun(false);
                        runToUse = loadedRun;
                      } catch (err) {
                        console.error('[PrimeAgentChat] Button handler: Failed to load approval run:', err);
                        setLoadingApprovalRun(false);
                        message.error('Failed to load approval details. Please try again.');
                        return;
                      }
                    }
                    
                    // Only show dialog if we have both approval and run (or if run is not needed)
                    if (runToUse || !pendingApproval?.agent_run_id) {
                      console.log('[PrimeAgentChat] Button handler: Setting showReviewDialog=true, runToUse:', !!runToUse);
                      setShowReviewDialog(true);
                    } else {
                      console.warn('[PrimeAgentChat] Button handler: Cannot show dialog - missing run');
                      message.warning('Loading approval details...');
                    }
                  }}
                >
                  Review Changes
                </Button>
              }
              style={{ marginTop: 16 }}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div
          style={{
            borderTop: '1px solid #f0f0f0',
            padding: '16px',
            backgroundColor: '#fafafa',
            flexShrink: 0,
          }}
        >
          <FocusAreaSelector
            selectedAreas={focusAreas}
            onSelectionChange={setFocusAreas}
            approvalStatus={focusAreaStatus}
          />
          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask about or request changes to the artifact..."
              rows={3}
              disabled={loading || !thread}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={loading}
              disabled={!thread}
              style={{ height: 'auto' }}
            >
              Send
            </Button>
          </Space.Compact>
        </div>
      </Card>

      {/* Review Dialog */}
      {(() => {
        const canShowDialog = showReviewDialog && pendingApproval && pendingApprovalRun;
        if (showReviewDialog && !canShowDialog) {
          console.log('[PrimeAgentChat] Dialog should show but conditions not met:', {
            showReviewDialog,
            hasPendingApproval: !!pendingApproval,
            hasPendingApprovalRun: !!pendingApprovalRun,
            approvalId: pendingApproval?.id,
            agentRunId: pendingApproval?.agent_run_id,
          });
        }
        // Extract focus areas from approval metadata
        const focusAreas = (pendingApproval?.artifact_references?.focus_areas as string[]) || [];
        
        return canShowDialog ? (
          <ArtifactReviewDialog
            approval={pendingApproval}
            run={pendingApprovalRun}
            artifactId={artifactId}
            focusAreas={focusAreas}
            open={showReviewDialog}
            onClose={() => setShowReviewDialog(false)}
            onApprovalDecision={handleApprovalDecision}
          />
        ) : null;
      })()}
    </div>
  );
};

